<template>
  <lb-dialog
    v-model="internalValue"
    heading="Revenue Breakup"
    :width="width"
    :loading="loading"
  >
    <template v-slot:body>
      <div>
        <div v-if="!loading && $store.state.aiEnabled && revenueCommentaryGenAiBtntab">
          <v-row>
            <v-col>
              <div v-if="revenueAILoadingProforma" class="mt-2 pa-3">
                <v-skeleton-loader
                  v-if="revenueAILoadingProforma"
                  type="paragraph,paragraph,paragraph,paragraph"
                  class="skeleton-textarea"
                ></v-skeleton-loader>
              </div>
              <div v-else>
                <div
                  class="font-size-md pl-2 mb-2 noTransform"
                  :style="`border-left: 4px solid ${tablecolors.heading.a} !important`"
                >
                  {{ actualLabel }} Vs {{ proformaLabel }}
                </div>
                <v-textarea
                  class="mt-2"
                  height="200"
                  outlined
                  :value="revenueAIProforma"
                  label="Local Commentary Mandatory"
                  @input="$emit('update:revenue-ai-proforma', $event)"
                ></v-textarea>
              </div>
            </v-col>
            <v-col>
              <div v-if="revenueAILoadingForecast" class="mt-2 pa-3">
                <v-skeleton-loader
                  v-if="revenueAILoadingForecast"
                  type="paragraph,paragraph,paragraph,paragraph"
                  class="skeleton-textarea"
                ></v-skeleton-loader>
              </div>
              <div v-else>
                <div
                  class="font-size-md pl-2 mb-2 noTransform"
                  :style="`border-left: 4px solid ${tablecolors.heading.b} !important`"
                >
                  {{ actualLabel }} Vs {{ forecastLabel }}
                </div>
                <v-textarea
                  class="mt-2"
                  height="200"
                  outlined
                  :value="revenueAIForecast"
                  label="Local Commentary Mandatory"
                  @input="$emit('update:revenue-ai-forecast', $event)"
                ></v-textarea>
              </div>
            </v-col>
          </v-row>
          <div class="d-flex justify-end">
            <v-btn
              color="primary"
              class="mb-3"
              :disabled="isTyping || revenueAILoadingProforma || revenueAILoadingForecast || isThresholdExceedRevenueCommentary"
              @click="emitGenerateAi('One', 'proforma')"
            >
              <v-icon dark left>mdi-creation</v-icon>
              <span class="noTransform">Gen AI</span>
            </v-btn>
            <v-btn
              color="primary"
              class="mb-3 ml-3"
              :disabled="isTyping || revenueAILoadingProforma || revenueAILoadingForecast || isThresholdExceedRevenueCommentary"
              @click="emitGenerateAi('Two', 'forecast')"
            >
              <v-icon dark left>mdi-creation</v-icon>
              <span class="noTransform">Gen AI</span>
            </v-btn>
          </div>
        </div>

        <div class="d-flex align-center mb-4">
          <v-btn-toggle
            v-if="hasBreakdown"
            v-model="viewMode"
            mandatory
            dense
            class="elevation-0"
            color="primary"
          >
            <v-btn value="grouped"><span>Grouped</span></v-btn>
            <v-btn value="ungrouped"><span>Ungrouped</span></v-btn>
          </v-btn-toggle>
          <v-spacer />
        </div>
        <div class="d-flex" v-if="showUploadButton">
          <v-btn color="secondaryBtn" class="mb-2" @click="emitOpenUpload()"><span>Upload Commentary</span></v-btn>
        </div>
        <div v-if="showGroupedTable" class="mb-4 table-wrapper">
          <div style="max-height:400px;overflow-y:auto;">
            <v-data-table
              :headers="clientTypeHeaders"
              :items="breakdownData"
              item-key="_id"
              class="elevation-1"
              fixed
              :loading="loading"
              hide-default-footer
              fixed-header
              height="400px"
            >
              <template v-slot:item="{ item }">
                <tr
                  @click="item._id !== total ? handleToggleGroup(item._id) : null"
                  class="parent-row"
                  v-if="isParent(item)"
                >
                  <td>
                    <div class="d-flex align-center">
                      <v-btn icon v-if="showToggleIcon(item)" class="toggle-btn mr-2">
                        <v-icon>{{ expandedGroups.includes(item._id) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                      </v-btn>
                      <span :class="item._id === total || (isSpecialRu && item._id === local) ? 'ml-10' : ''">
                        {{ item?._id ?? item?.subtotal?.cl_code ?? '-' }}
                      </span>
                    </div>
                  </td>
                  <td>{{ item.subtotal.cl_code ?? '' }}</td>
                  <td>{{ $nova.formatNumber(item.subtotal.actual_amount) }}</td>
                  <td>{{ $nova.formatNumber(item.subtotal.proforma_amount) }}</td>
                  <td>{{ $nova.formatNumber(item.subtotal.actual_vs_proforma_variance_amount) }}</td>
                  <td>{{ item.subtotal.commentary1 ?? '-' }}</td>
                  <td>{{ $nova.formatNumber(item.subtotal.forecast_amount) }}</td>
                  <td>{{ $nova.formatNumber(item.subtotal.actual_vs_forecast_variance_amount) }}</td>
                  <td>{{ item.subtotal.commentary2 ?? '-' }}</td>
                </tr>
                <template v-if="isExpanded(item)">
                  <tr v-for="client in item.documents" :key="client._id" class="child-row">
                    <td><div class="pl-8">{{ client?.client_name ?? client?.cl_code ?? '-' }}</div></td>
                    <td>{{ client.cl_code ?? '' }}</td>
                    <td>{{ $nova.formatNumber(client.actual_amount) }}</td>
                    <td>{{ $nova.formatNumber(client.proforma_amount) }}</td>
                    <td>{{ $nova.formatNumber(client.actual_vs_proforma_variance_amount) }}</td>
                    <td>{{ client.commentary1 ?? '-' }}</td>
                    <td>{{ $nova.formatNumber(client.forecast_amount) }}</td>
                    <td>{{ $nova.formatNumber(client.actual_vs_forecast_variance_amount) }}</td>
                    <td>{{ client.commentary2 ?? '-' }}</td>
                  </tr>
                </template>
              </template>
            </v-data-table>
          </div>
        </div>
        <div v-else class="mb-4 table-wrapper-ungrouped">
          <lb-datatablenew
            v-if="clientByClientHasData"
            :headers="clientByClientHeaders"
            :items="clientbyclientData"
            :loading="loading"
            hide-default-footer
            item-key="_id"
            class="elevation-1"
            fixed-header
            height="auto"
          ></lb-datatablenew>
        </div>
      </div>
    </template>
  </lb-dialog>
</template>

<script>
import varianceAnalysisConstants from '../constants';

export default {
  name: 'RevenueBreakupDialog',
  props: {
    value: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    width: { type: [String, Number], default: 1500 },
    breakdownData: { type: Array, default: () => [] },
    revenueViewMode: { type: String, default: 'grouped' },
    expandedGroups: { type: Array, default: () => [] },
    clientbyclientData: { type: Array, default: () => [] },
    clientbyclientFullData: { type: Array, default: () => [] },
    clientTypeHeaders: { type: Array, default: () => [] },
    clientByClientHeaders: { type: Array, default: () => [] },
    ispreparer: { type: Boolean, default: false },
    isPendingLocal: { type: Boolean, default: false },
    isPendingRegional: { type: Boolean, default: false },
    isSpecialRu: { type: Boolean, default: false },
    netSalesConstants: { type: Object, default: () => varianceAnalysisConstants.CLIENT_SALES_BREAKDOWN },
    tablecolors: { type: Object, default: () => ({ heading: { a: '#d0dcff', b: '#efcef4' }, body: { a: '', b: '' } }) },
    selectedCommentary: { type: Object, default: () => ({}) },
    revenueAIProforma: { type: String, default: '' },
    revenueAIForecast: { type: String, default: '' },
    revenueAILoadingProforma: { type: Boolean, default: false },
    revenueAILoadingForecast: { type: Boolean, default: false },
    isTyping: { type: Boolean, default: false },
    isThresholdExceedRevenueCommentary: { type: Boolean, default: false },
    revenueCommentaryGenAiBtntab: { type: Boolean, default: false },
    actualLabel: { type: String, default: 'Actual' },
    proformaLabel: { type: String, default: 'Proforma' },
    forecastLabel: { type: String, default: 'Forecast' },
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    viewMode: {
      get() {
        return this.revenueViewMode;
      },
      set(value) {
        this.$emit('update:revenue-view-mode', value);
      },
    },
    hasBreakdown() {
      return this.breakdownData && this.breakdownData.length > 0;
    },
    showUploadButton() {
      return (
        this.ispreparer &&
        this.clientbyclientFullData.length > 0 &&
        (this.isPendingLocal || this.isPendingRegional)
      );
    },
    showGroupedTable() {
      return this.hasBreakdown && this.viewMode === 'grouped';
    },
    total() {
      return varianceAnalysisConstants.CLIENT_SALES_BREAKDOWN.CL_TYPES.TOTAL;
    },
    local() {
      return varianceAnalysisConstants.CLIENT_SALES_BREAKDOWN.CL_TYPES.LOCAL;
    },
    clientByClientHasData() {
      return this.clientbyclientData && this.clientbyclientData.length > 0 && !this.loading;
    },
  },
  methods: {
    emitOpenUpload() {
      this.$emit('open-upload-commentary');
    },
    handleToggleGroup(groupId) {
      this.$emit('toggle-revenue-group', groupId);
    },
    isParent(item) {
      const { CL_TYPES } = varianceAnalysisConstants.CLIENT_SALES_BREAKDOWN;
      return (
        item?._id !== CL_TYPES.OTHERS &&
        item?._id !== CL_TYPES.OTHER &&
        item?._id !== CL_TYPES.SUBTOTAL
      );
    },
    showToggleIcon(item) {
      const { CL_TYPES } = varianceAnalysisConstants.CLIENT_SALES_BREAKDOWN;
      return (
        item._id !== this.total &&
        !(this.isSpecialRu && item._id === this.local) &&
        item._id !== CL_TYPES.OTHERS &&
        item._id !== CL_TYPES.OTHER &&
        item._id !== CL_TYPES.SUBTOTAL
      );
    },
    isExpanded(item) {
      return this.expandedGroups.includes(item._id) || this.openGroupsForOthers(item);
    },
    openGroupsForOthers(item) {
      const { CL_TYPES } = varianceAnalysisConstants.CLIENT_SALES_BREAKDOWN;
      return (
        (item?._id === CL_TYPES.OTHERS || item?._id === CL_TYPES.OTHER) &&
        this.expandedGroups.includes(item._id)
      );
    },
    emitGenerateAi(mode, forecastType) {
      this.$emit('generate-ai', { mode, forecastType, commentary: this.selectedCommentary });
    },
  },
};
</script>
